import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { useAgentStore } from '../../store/agentStore.jsx';
import StatusBadge from '../shared/StatusBadge.jsx';
import SyslogRulesSection from './SyslogRulesSection.jsx';

import { TableSkeleton } from '../shared/Skeleton.jsx';
const CONDITION_TYPES = [
  { value: 'threshold', label: 'Threshold' },
  { value: 'status_change', label: 'Status Change' },
  { value: 'ping_loss', label: 'Ping Loss' },
  { value: 'interface_down', label: 'Interface Down' },
  { value: 'cpu_high', label: 'CPU High' },
  { value: 'memory_high', label: 'Memory High' },
];

export default function AlertRuleManager() {
  const { agentId } = useAgentStore();
  const queryClient = useQueryClient();

  const { data: rules, isLoading } = useQuery({
    queryKey: ['alert-rules', agentId],
    queryFn: () => api.get('/alert-rules', { agent_id: agentId }),
    enabled: !!agentId,
    // #676: /alert-rules now returns the cursor envelope
    // {data,cursor,error}. Unwrap to the array (established
    // `resp?.data ?? resp` pattern). The manager renders the first page
    // only; the default limit is 100 rules, which comfortably covers a
    // per-agent rule set. If an org ever exceeds 100 rules on one agent,
    // this table would silently show only the first 100 — cursor-follow
    // would need adding then.
    select: (resp) => {
      const items = resp?.data ?? resp;
      return Array.isArray(items) ? items : [];
    },
  });

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: '',
    severity: 'warning',
    condition_type: 'threshold',
    condition_value: '',
    target_type: 'all',
    target_value: '',
    // S14-7 vista#464: defaults from the agent + cloud migrations.
    // Surfaced in the form so operators see the values they'll commit
    // to even when leaving the defaults.
    min_dwell_secs: 60,
    hysteresis_band: 5.0,
  });

  const createMutation = useMutation({
    mutationFn: (data) => api.post('/alert-rules', {
      ...data,
      agent_id: agentId,
      condition_value: data.condition_value ? JSON.parse(data.condition_value) : {},
      // S14-7: send debounce only for threshold rules — non-threshold
      // rules ignore the columns on the agent side, but trimming the
      // payload keeps the cloud's PUT diff cleaner.
      min_dwell_secs: data.condition_type === 'threshold' ? Number(data.min_dwell_secs) : undefined,
      hysteresis_band: data.condition_type === 'threshold' ? Number(data.hysteresis_band) : undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
      setShowForm(false);
      setForm({
        name: '',
        severity: 'warning',
        condition_type: 'threshold',
        condition_value: '',
        target_type: 'all',
        target_value: '',
        min_dwell_secs: 60,
        hysteresis_band: 5.0,
      });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ ruleId, enabled }) => api.post(`/alert-rules/${ruleId}`, { enabled, agent_id: agentId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alert-rules'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (ruleId) => api.delete(`/alert-rules/${ruleId}`, null, { agent_id: agentId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alert-rules'] }),
  });

  if (!agentId) return <p className="text-sm text-gray-400">Select an agent to manage alert rules.</p>;
  if (isLoading) return <TableSkeleton rows={5} cols={5} message="Loading alert rules…" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">Alert Rules</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
        >
          {showForm ? 'Cancel' : 'Create Rule'}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={(e) => { e.preventDefault(); createMutation.mutate(form); }}
          className="space-y-3 rounded-lg border border-gray-200 bg-white p-4"
        >
          <div className="grid grid-cols-2 gap-3">
            <input type="text" placeholder="Rule name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-md border border-gray-300 px-3 py-2 text-sm" required />
            <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="rounded-md border border-gray-300 px-3 py-2 text-sm">
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
            <select value={form.condition_type} onChange={(e) => setForm({ ...form, condition_type: e.target.value })} className="rounded-md border border-gray-300 px-3 py-2 text-sm">
              {CONDITION_TYPES.map((ct) => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
            </select>
            <select value={form.target_type} onChange={(e) => setForm({ ...form, target_type: e.target.value })} className="rounded-md border border-gray-300 px-3 py-2 text-sm">
              <option value="all">All Devices</option>
              <option value="device">Specific Device</option>
              <option value="group">Device Group</option>
            </select>
          </div>
          {form.target_type !== 'all' && (
            <input type="text" placeholder={form.target_type === 'device' ? 'Device ID' : 'Group name'} value={form.target_value} onChange={(e) => setForm({ ...form, target_value: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
          )}
          <textarea placeholder='Condition config JSON (e.g., {"threshold": 90, "metric": "cpu"})' value={form.condition_value} onChange={(e) => setForm({ ...form, condition_value: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono" rows={2} />

          {/* S14-7 vista#464: threshold-rule debounce + hysteresis.
              Hidden for non-threshold rules (status_change, ping_loss,
              interface_down, etc.) — those condition types don't run
              through the dwell+hysteresis path on the agent. */}
          {form.condition_type === 'threshold' && (
            <div className="grid grid-cols-2 gap-3 rounded-md border border-amber-100 bg-amber-50/40 p-3">
              <label className="block text-xs text-gray-700">
                <span className="block font-medium">Min dwell (seconds)</span>
                <span
                  className="block text-[11px] text-gray-500"
                  title="Score must stay below threshold this long before the alert fires. 60s eliminates noise from short oscillations."
                >
                  Wait this long below threshold before firing. Default 60s.
                </span>
                <input
                  type="number"
                  min={0}
                  max={3600}
                  step={1}
                  value={form.min_dwell_secs}
                  onChange={(e) => setForm({ ...form, min_dwell_secs: e.target.value })}
                  className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                  required
                />
              </label>
              <label className="block text-xs text-gray-700">
                <span className="block font-medium">Hysteresis band</span>
                <span
                  className="block text-[11px] text-gray-500"
                  title="Score must recover this far above the threshold before the alert clears. Prevents 31→30→31 thrash."
                >
                  Score must recover this far above threshold to clear. Default 5.0.
                </span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={form.hysteresis_band}
                  onChange={(e) => setForm({ ...form, hysteresis_band: e.target.value })}
                  className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                  required
                />
              </label>
            </div>
          )}

          <button type="submit" disabled={createMutation.isPending} className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50">
            {createMutation.isPending ? 'Creating...' : 'Create Rule'}
          </button>
        </form>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Severity</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Condition</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Target</th>
              {/* S14-8 vista#465: ack stats over the last 30 days. Operators
                  see at a glance which rules are firing into the void —
                  high count + low ack rate = a tuning candidate (likely
                  noise) or a process gap (operators not engaging with
                  the alert channel). */}
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500" title="Acknowledgements in the last 30 days">Acks (30d)</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500" title="Fraction of fires acknowledged in the last 30 days">Ack rate</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {(Array.isArray(rules) ? rules : []).length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-400">No alert rules configured</td>
              </tr>
            ) : (
              (Array.isArray(rules) ? rules : []).map((rule) => (
                <tr key={rule.rule_id}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{rule.name}</td>
                  <td className="px-4 py-3"><StatusBadge status={rule.severity} /></td>
                  <td className="px-4 py-3 text-sm text-gray-500">{rule.condition_type}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{rule.target_type === 'all' ? 'All' : `${rule.target_type}: ${rule.target_value}`}</td>
                  {/* S14-8 ack-stats columns. ack_rate_30d is NULL when
                      the rule has zero fires in the 30-day window (no
                      data to compute a rate from); render '—' in that
                      case so operators see "no fires" vs. "fires but 0
                      acks" as distinct states. The red dot marks rules
                      where ack_rate_30d < 0.1 AND fires_30d_count > 0 —
                      visible to operators in the moment as "this rule
                      generates noise nobody acts on." */}
                  <td className="px-4 py-3 text-sm text-gray-700 font-mono">
                    {rule.ack_count_30d != null ? rule.ack_count_30d : '—'}
                    {rule.ack_count_30d_fires != null && rule.ack_count_30d_fires > 0 && (
                      <span className="ml-1 text-xs text-gray-400">
                        / {rule.ack_count_30d_fires}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {rule.ack_rate_30d == null ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5">
                        {rule.ack_rate_30d < 0.1 && rule.ack_count_30d_fires > 0 && (
                          <span
                            className="inline-block h-2 w-2 rounded-full bg-red-500"
                            title="This rule generates noise nobody acts on — consider tuning thresholds, debounce (min_dwell_secs), or disabling."
                          />
                        )}
                        <span className="font-mono">{(rule.ack_rate_30d * 100).toFixed(0)}%</span>
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleMutation.mutate({ ruleId: rule.rule_id, enabled: !rule.enabled })}
                      disabled={toggleMutation.isPending}
                      className={`rounded px-2.5 py-0.5 text-xs font-medium disabled:opacity-50 ${rule.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                    >
                      {rule.enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => deleteMutation.mutate(rule.rule_id)}
                      disabled={deleteMutation.isPending}
                      className="rounded bg-red-100 px-2 py-1 text-xs text-red-600 hover:bg-red-200 disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* #500: federated syslog signature rules (agent-authoritative) —
          distinct from the D1 threshold rules above. */}
      <SyslogRulesSection />
    </div>
  );
}
